import { useState, useEffect } from "react";
import { Inbox, Mail, Phone, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MessagesTab = ({ getAuthHeaders }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contact/messages`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMessages(await response.json());
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Mensajes de Contacto</h2>
        <button
          onClick={fetchMessages}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay mensajes todavía</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Messages List */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedMessage(msg)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedMessage === msg ? "bg-orange-50 border-l-4 border-orange-500" : ""
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-gray-900">{msg.name}</span>
                    <span className="text-xs text-gray-500">{formatDate(msg.created_at)}</span>
                  </div>
                  <div className="text-sm text-orange-600 mb-1">{msg.subject}</div>
                  <div className="text-sm text-gray-500 line-clamp-2">{msg.message}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Message Detail */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            {selectedMessage ? (
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{selectedMessage.subject}</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-gray-600">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 font-medium text-sm">
                        {selectedMessage.name[0].toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium">{selectedMessage.name}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${selectedMessage.email}`} className="text-orange-500 hover:underline">
                      {selectedMessage.email}
                    </a>
                  </div>
                  
                  {selectedMessage.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${selectedMessage.phone}`} className="text-orange-500 hover:underline">
                        {selectedMessage.phone}
                      </a>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-400">
                    Recibido: {formatDate(selectedMessage.created_at)}
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Mensaje:</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                
                <div className="mt-6 flex gap-3">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex-1 text-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Responder por Email
                  </a>
                  {selectedMessage.phone && (
                    <a
                      href={`https://wa.me/${selectedMessage.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Selecciona un mensaje para ver los detalles</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesTab;
